import { useCallback } from "react"
import type { PropsWithChildren, ReactNode } from "react"
import { useOutsideClick } from "../../../hooks/useOutsideClick"
import { useMessagingView } from "../hooks"
import { classNames } from "../utils"
import styles from "./Menu.module.scss"

export enum VerticalPosition {
    Top = "top",
    Bottom = "bottom",
}

export enum HorizontalPosition {
    Left = "left",
    Right = "right",
    Center = "center",
}

const positionClassMap = {
    [VerticalPosition.Top]: styles.positionTop,
    [VerticalPosition.Bottom]: styles.positionBottom,
    [HorizontalPosition.Left]: styles.positionLeft,
    [HorizontalPosition.Right]: styles.positionRight,
    [HorizontalPosition.Center]: styles.positionCenter,
}

type MenuProps = {
    /** Content for the toggle button */
    toggle?: ReactNode
    /** Additional class for the toggle button */
    toggleClassName?: string
    /** Additional class for the menu */
    menuClassName?: string
    /** Whether the menu is open */
    isOpen: boolean
    /** Callback when open state changes */
    onOpenChange: (isOpen: boolean) => void
    /** Vertical position of the menu */
    verticalPosition?: VerticalPosition
    /** Horizontal position of the menu */
    horizontalPosition?: HorizontalPosition
}

/* Menu component that serves as the container for menu items */
export function Menu({
    children,
    toggle,
    toggleClassName,
    menuClassName,
    isOpen,
    onOpenChange,
    verticalPosition = VerticalPosition.Bottom,
    horizontalPosition = HorizontalPosition.Left,
    ...props
}: PropsWithChildren<MenuProps & React.HTMLAttributes<HTMLDivElement>>) {
    const messagingView = useMessagingView()
    const isDesktop = !messagingView.isMobile
    const handleToggle = useCallback(() => {
        onOpenChange(!isOpen)
    }, [isOpen, onOpenChange])

    const closeMenu = useCallback(() => {
        onOpenChange(false)
    }, [onOpenChange])

    const menuRef = useOutsideClick(closeMenu)

    return (
        <div
            ref={menuRef}
            className={classNames(styles.menuContainer, {
                [styles.desktop]: isDesktop,
            })}
            data-testid="message-menu-container"
            {...props}
        >
            {toggle && (
                <div
                    className={toggleClassName}
                    onClick={handleToggle}
                    data-testid="menu-toggle"
                >
                    {toggle}
                </div>
            )}
            {isOpen && (
                <div
                    className={classNames(
                        styles.menu,
                        positionClassMap[verticalPosition],
                        positionClassMap[horizontalPosition],
                        menuClassName,
                    )}
                    data-testid="menu-list"
                >
                    {children}
                </div>
            )}
        </div>
    )
}
